import { callApi } from 'src/core/callApi';
import { AI_SEARCH_API_URL } from 'src/utils/const';

export type AiSearchCreateResponse = {
  message_id: string;
  status: string;
  created_at: number;
  deadline_at: number;
  search_plan?: AiSearchSearchPlan;
};

export type AiSearchSearchPlanCategory =
  | 'market_scan'
  | 'market_analysis'
  | 'property_analysis'
  | 'terms_definition'
  | 'not_supported';

export type AiSearchSearchPlan = {
  version: number;
  category: AiSearchSearchPlanCategory;
  normalized_query: string;
  market_terms?: string[];
  market_places?: Array<{
    name: string;
    latitude?: number;
    longitude?: number;
    country_code?: string;
  }>;
  scan_market_places?: Array<{
    name: string;
    latitude?: number;
    longitude?: number;
    country_code?: string;
  }>;
  region_terms?: string[];
  scan_country_codes?: string[];
  scan_market_terms?: string[];
  filters?: {
    bedrooms?: string[] | number | null;
    bathrooms?: string[] | number | null;
    property_type?: string[] | string | null;
    amenity?: Record<string, boolean>;
    price_tier?: string | null;
    segment?: string | null;
  };
  property_context?: {
    address?: string;
    bedrooms?: number | null;
    bathrooms?: number | null;
    accommodates?: number | null;
  };
  reason?: string;
  stage1_status: 'ready' | 'unsupported';
  stage1_message: string;
  market_resolution?: {
    input_terms?: string[];
    resolved_markets?: Array<{
      market_id: number;
      name: string;
      region: string;
      country: string;
      country_code: string;
      source_term?: string;
    }>;
    selection_policy?: string;
    limit?: number;
  };
};

export type AiSearchCoordinatesFallback = {
  used: boolean;
  status?: 'sampled' | 'low_confidence' | 'no_listing_sample';
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  confidence?: number;
  listing_sample_count?: number;
  total_count?: number;
  bounds?: {
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  };
};

export type AiSearchFollowUpContext = {
  type: 'property_bedrooms';
  property_address: string;
};

export type AiSearchRequestContext = {
  follow_up_context?: AiSearchFollowUpContext;
};

export type AiSearchConversationTurn = {
  role: 'user' | 'assistant';
  text: string;
};

export type AiSearchCreateRequest = {
  context?: AiSearchRequestContext;
  conversation_history?: AiSearchConversationTurn[];
  suppress_slack?: boolean;
};

export type AiSearchResponseMetadata = Record<string, unknown> & {
  coordinates_fallback_used?: boolean;
  coordinates_fallback?: AiSearchCoordinatesFallback;
  follow_up_context?: AiSearchFollowUpContext;
};

export type AiSearchReadResponse = {
  message_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'failed_timeout';
  created_at: number;
  deadline_at: number;
  search_plan?: AiSearchSearchPlan;
  answer?: string;
  metadata?: AiSearchResponseMetadata;
  error?: {
    code: string;
    message: string;
  };
};

export type AiSearchApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; status?: number };

const AI_SEARCH_CREATE_PATH = `${AI_SEARCH_API_URL}/ai-search/message/create`;
const AI_SEARCH_READ_PATH = `${AI_SEARCH_API_URL}/ai-search/message/read`;

const formatApiError = (response: Record<string, unknown> | null | undefined): string => {
  if (!response) {
    return 'Request failed';
  }
  const message = response.message;
  if (typeof message === 'string') {
    return message;
  }
  if (message && typeof message === 'object' && 'error' in (message as object)) {
    const nested = (message as { error?: { message?: string } }).error;
    if (nested?.message) {
      return nested.message;
    }
  }
  const err = response.error;
  if (typeof err === 'string') {
    return err;
  }
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string') {
    return (err as { message: string }).message;
  }
  const status = response.status;
  if (typeof status === 'number') {
    return `Request failed (${status})`;
  }
  return 'Request failed';
};

export const createAiSearchMessage = async (
  question: string,
  request?: AiSearchCreateRequest
): Promise<AiSearchApiResult<AiSearchCreateResponse>> => {
  const payload: {
    question: string;
    context?: AiSearchRequestContext;
    conversation_history?: AiSearchConversationTurn[];
    suppress_slack?: boolean;
  } = { question };
  if (request?.context) {
    payload.context = request.context;
  }
  if (request?.conversation_history && request.conversation_history.length > 0) {
    payload.conversation_history = request.conversation_history;
  }
  if (request?.suppress_slack) {
    payload.suppress_slack = true;
  }

  const response = await callApi(
    AI_SEARCH_CREATE_PATH,
    {
      body: payload,
      aiSafeMode: true,
    },
    true,
    false,
    '',
    'POST',
    true
  );

  if (response?.message && !response.error) {
    return { ok: true, data: response.message as AiSearchCreateResponse };
  }
  return {
    ok: false,
    error: formatApiError(response),
    status: typeof response?.status === 'number' ? response.status : undefined,
  };
};

export const readAiSearchMessage = async (
  messageId: string
): Promise<AiSearchApiResult<AiSearchReadResponse>> => {
  const response = await callApi(
    AI_SEARCH_READ_PATH,
    {
      queryParams: {
        message_id: encodeURIComponent(messageId),
      },
      aiSafeMode: true,
    },
    true,
    false,
    '',
    'GET',
    true
  );

  if (response?.message && !response.error) {
    return { ok: true, data: response.message as AiSearchReadResponse };
  }
  return {
    ok: false,
    error: formatApiError(response),
    status: typeof response?.status === 'number' ? response.status : undefined,
  };
};
